import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Search,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  CreditCard,
} from "lucide-react";
import DataTable from "@/components/mui/DataTable";

export default function PDV() {
  const [productOptions, setProductOptions] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [currentCashier, setCurrentCashier] = useState(null);
  const [promissoryData, setPromissoryData] = useState({
    parcelar: false,
    parcelas: 1,
    datas: [new Date().toISOString().split("T")[0]],
  });

  // orçamento
  const [isBudget, setIsBudget] = useState(false);

  // Opções de finalização
  const [gerarNotaFiscal, setGerarNotaFiscal] = useState(false);
  const [imprimirComprovante, setImprimirComprovante] = useState(true);
  const [systemConfigs, setSystemConfigs] = useState({});

  // modal de senha de supervisor
  const [supervisorDialogOpen, setSupervisorDialogOpen] = useState(false);
  const [supervisorPassword, setSupervisorPassword] = useState("");
  const [verifyingSupervisor, setVerifyingSupervisor] = useState(false);

  // estado de envio da venda/orçamento
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSystemConfigs();
  }, []);

  const normalizeMoney = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return 0;

      const normalized = trimmed
        .replace(/\s/g, "")
        .replace("R$", "")
        .replace(/\.(?=\d{3}(\D|$))/g, "")
        .replace(",", ".");

      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  };

  const buildPromissoryDates = (parcelas) => {
    const totalParcelas = Math.max(1, Number(parcelas) || 1);
    const today = new Date();
    const datas = [];

    for (let i = 0; i < totalParcelas; i += 1) {
      const date = new Date(today);
      date.setMonth(date.getMonth() + i);
      datas.push(date.toISOString().split("T")[0]);
    }

    return datas;
  };

  const resetPromissoryData = () => {
    setPromissoryData({
      parcelar: false,
      parcelas: 1,
      datas: [new Date().toISOString().split("T")[0]],
    });
  };

  const handlePromissoryChange = (field, value) => {
    if (field === "parcelas") {
      const parcelas = Math.max(1, Number(value) || 1);
      setPromissoryData((prev) => ({
        ...prev,
        parcelas,
        datas: buildPromissoryDates(parcelas),
      }));
      return;
    }

    if (field === "parcelar") {
      setPromissoryData((prev) => ({
        ...prev,
        parcelar: value,
        parcelas: value ? Math.max(1, Number(prev.parcelas) || 1) : 1,
        datas: value
          ? buildPromissoryDates(Math.max(1, Number(prev.parcelas) || 1))
          : [prev.datas?.[0] || new Date().toISOString().split("T")[0]],
      }));
      return;
    }

    setPromissoryData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePromissoryDateChange = (index, value) => {
    setPromissoryData((prev) => {
      const datas = [...(prev.datas || [])];
      datas[index] = value;
      return { ...prev, datas };
    });
  };

  const fetchData = async () => {
    try {
      const [customersRes] = await Promise.all([
        axios.get(`${API}/customers`),
      ]);
      setCustomers(customersRes.data.data || customersRes.data);

      // Caixa atual aberto, se existir
      try {
        const cashierRes = await axios.get(`${API}/cashiers/current`);
        setCurrentCashier(cashierRes.data);
      } catch (error) {
        // Nenhum caixa aberto
      }
    } catch (error) {
      toast.error("Erro ao carregar dados");
    }
  };

  const fetchSystemConfigs = async () => {
    try {
      const res = await axios.get(`${API}/system-config`);
      setSystemConfigs(res.data);
      setGerarNotaFiscal(res.data.gerar_nota_fiscal_default || false);
      setImprimirComprovante(res.data.imprimir_comprovante_default !== false);
    } catch (error) {
      console.log("Configs não carregadas");
    }
  };

  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      setProductOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setSearchingProducts(true);
        const response = await axios.get(`${API}/products`, {
          params: {
            search: term,
            per_page: 30,
            sort_by: "name",
            sort_dir: "asc",
          },
        });
        setProductOptions(response.data?.data || response.data || []);
      } catch (error) {
        setProductOptions([]);
      } finally {
        setSearchingProducts(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const searchProduct = async () => {
    const term = searchTerm.trim();
    if (!term) return;

    try {
      const digitsTerm = term.replace(/\D/g, "");
      if (digitsTerm.length >= 4) {
        try {
          const barcodeResponse = await axios.get(`${API}/products/barcode/${digitsTerm}`);
          if (barcodeResponse.data?.id) {
            addToCart(barcodeResponse.data);
            setSearchTerm("");
            setProductOptions([]);
            return;
          }
        } catch (error) {
          // Fallback para busca parcial.
        }
      }

      const response = await axios.get(`${API}/products`, {
        params: {
          search: term,
          per_page: 1,
          sort_by: "name",
          sort_dir: "asc",
        },
      });
      const resultList = response.data?.data || response.data || [];
      const product = resultList[0];

      if (product) {
        addToCart(product);
        setSearchTerm("");
        setProductOptions([]);
      } else {
        toast.error("Produto não encontrado");
      }
    } catch (error) {
      toast.error("Erro ao buscar produto");
    }
  };

  const addToCart = (product) => {
    const unitPrice = normalizeMoney(product.sale_price);
    setCart((currentCart) => {
      const existingItem = currentCart.find((item) => item.product_id === product.id);

      if (existingItem) {
        return currentCart.map((item) =>
          item.product_id === product.id
            ? {
                ...item,
                quantity: item.quantity + 1,
                unit_price: normalizeMoney(item.unit_price),
                total: (item.quantity + 1) * normalizeMoney(item.unit_price),
              }
            : item
        );
      }

      return [
        ...currentCart,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: unitPrice,
          total: unitPrice,
        },
      ];
    });
    toast.success(`${product.name} adicionado ao carrinho`);
  };

  const updateQuantity = (productId, delta) => {
    setCart((currentCart) =>
      currentCart
        .map((item) => {
          if (item.product_id === productId) {
            const newQuantity = item.quantity + delta;
            if (newQuantity <= 0) return null;
            const unitPrice = normalizeMoney(item.unit_price);
            return {
              ...item,
              quantity: newQuantity,
              unit_price: unitPrice,
              total: newQuantity * unitPrice,
            };
          }
          return item;
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (productId) => {
    setCart((currentCart) =>
      currentCart.filter((item) => item.product_id !== productId)
    );
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      const unitPrice = normalizeMoney(item.unit_price);
      const quantity = Number(item.quantity) || 0;
      const itemTotal = normalizeMoney(item.total) || unitPrice * quantity;

      return sum + itemTotal;
    }, 0);
  };

  const calculateTotal = () => {
    return Math.max(0, calculateSubtotal() - normalizeMoney(discount));
  };

  /**
   * Função que realmente envia a venda/orçamento para o backend
   */
  const finalizeSale = async () => {
    if (cart.length === 0) {
      toast.error("Carrinho vazio");
      return;
    }

    if (paymentMethod === "promissoria" && !selectedCustomer) {
      toast.error("Para venda com promissória, selecione um cliente.");
      return;
    }

    if (paymentMethod === "promissoria") {
      const parcelas = promissoryData.parcelar
        ? Math.max(1, Number(promissoryData.parcelas) || 1)
        : 1;
      const datas = (promissoryData.datas || []).slice(0, parcelas);

      if (datas.length !== parcelas || datas.some((data) => !data)) {
        toast.error("Preencha todas as datas de vencimento da promissória.");
        return;
      }
    }

    if (!currentCashier) {
      toast.error(
        "Nenhum caixa aberto. Por favor, abra um caixa antes de fazer vendas."
      );
      return;
    }

    try {
      setFinalizing(true);

      const normalizedCart = cart.map((item) => {
        const unitPrice = normalizeMoney(item.unit_price);
        const quantity = Number(item.quantity) || 0;

        return {
          ...item,
          quantity,
          unit_price: unitPrice,
          total: unitPrice * quantity,
        };
      });

      const saleData = {
        customer_id: selectedCustomer || null,
        items: normalizedCart,
        discount: normalizeMoney(discount),
        payment_method: paymentMethod,
        promissory_data:
          paymentMethod === "promissoria"
            ? {
                parcelar: promissoryData.parcelar,
                parcelas: promissoryData.parcelar
                  ? Math.max(1, Number(promissoryData.parcelas) || 1)
                  : 1,
                datas: (promissoryData.datas || []).slice(
                  0,
                  promissoryData.parcelar
                    ? Math.max(1, Number(promissoryData.parcelas) || 1)
                    : 1
                ),
              }
            : undefined,
        cashier_id: currentCashier.id,
        is_budget: isBudget,
        gerar_nota_fiscal: gerarNotaFiscal && !isBudget,
        fiscal_document_type: gerarNotaFiscal && !isBudget ? "nfce" : undefined,
        imprimir_comprovante: imprimirComprovante,
      };

      const response = await axios.post(`${API}/sales`, saleData);

      toast.success(
        isBudget
          ? "Orçamento salvo com sucesso!"
          : "Venda finalizada com sucesso!"
      );

      // Imprimir comprovante se solicitado
      if (imprimirComprovante && !isBudget && response.data?.id) {
        try {
          const receiptRes = await axios.get(`${API}/sales/${response.data.id}/receipt`);
          printReceipt(receiptRes.data);
        } catch (e) {
          console.log("Erro ao imprimir comprovante");
        }
      }

      // Reset
      setCart([]);
      setSelectedCustomer("");
      setDiscount(0);
      setSearchTerm("");
      setIsBudget(false); // volta para venda normal
      setPaymentMethod("cash");
      resetPromissoryData();
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      const backendError = error?.response?.data?.error;
      toast.error(backendError || backendMessage || "Erro ao finalizar venda");
    } finally {
      setFinalizing(false);
    }
  };

  /**
   * Handler do botão "Finalizar Venda / Salvar Orçamento"
   * - Se desconto <= 3% do subtotal → finaliza direto
   * - Se desconto > 3% do subtotal → pede senha do supervisor
   */
  const handleFinalizeClick = () => {
    if (cart.length === 0) {
      toast.error("Carrinho vazio");
      return;
    }

    if (paymentMethod === "promissoria" && !selectedCustomer) {
      toast.error("Para venda com promissória, selecione um cliente.");
      return;
    }

    if (!currentCashier) {
      toast.error(
        "Nenhum caixa aberto. Por favor, abra um caixa antes de fazer vendas."
      );
      return;
    }

    const subtotal = calculateSubtotal();
    if (subtotal <= 0) {
      toast.error("Subtotal inválido.");
      return;
    }

    const maxDiscountWithoutSupervisor = subtotal * 0.03; // 3%

    if (discount > maxDiscountWithoutSupervisor) {
      // precisa de senha de supervisor
      setSupervisorDialogOpen(true);
    } else {
      // dentro de 3% → pode seguir sem senha
      finalizeSale();
    }
  };

  /**
   * Cancelar autorização do supervisor
   * - Fecha modal
   * - Zera o desconto
   */
  const handleSupervisorCancel = () => {
    if (verifyingSupervisor || finalizing) return;
    setSupervisorDialogOpen(false);
    setSupervisorPassword("");
    setDiscount(0);
  };

  /**
   * Confirmar senha de supervisor
   * - Verifica na rota /users/check-supervisor-password
   * - Se ok → finaliza venda/orçamento
   */
  const handleSupervisorConfirm = async () => {
    if (!supervisorPassword) {
      toast.error("Informe a senha do supervisor.");
      return;
    }

    try {
      setVerifyingSupervisor(true);

      await axios.post(`${API}/users/check-supervisor-password`, {
        password: supervisorPassword,
      });

      setSupervisorDialogOpen(false);
      setSupervisorPassword("");

      await finalizeSale();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Senha do supervisor inválida.";
      toast.error(msg);
    } finally {
      setVerifyingSupervisor(false);
    }
  };

  const formatMoney = (value) =>
    `R$ ${normalizeMoney(value).toFixed(2).replace(".", ",")}`;

  // Imprimir comprovante (abre janela de impressão)
  const printReceipt = (receipt) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Comprovante de Venda</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; padding: 10px; max-width: 300px; margin: 0 auto; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { border-top: 1px dashed #000; margin: 8px 0; }
          table { width: 100%; }
          td { padding: 2px 0; }
          .right { text-align: right; }
        </style>
      </head>
      <body>
        <div class="center bold">${receipt.empresa?.nome || 'EMPRESA'}</div>
        <div class="center">${receipt.empresa?.cnpj || ''}</div>
        <div class="center">${receipt.empresa?.endereco || ''}</div>
        <div class="center">${receipt.empresa?.telefone || ''}</div>
        <div class="divider"></div>
        <div class="center bold">CUPOM NÃO FISCAL</div>
        <div>Venda #${receipt.venda?.numero}</div>
        <div>Data: ${receipt.venda?.data}</div>
        <div>Vendedor: ${receipt.venda?.vendedor || '-'}</div>
        ${receipt.cliente ? `<div>Cliente: ${receipt.cliente.nome}</div>` : ''}
        <div class="divider"></div>
        <table>
          ${receipt.itens?.map(item => `
            <tr>
              <td colspan="2">${item.descricao}</td>
            </tr>
            <tr>
              <td>${item.quantidade} x R$ ${item.valor_unitario}</td>
              <td class="right">R$ ${item.subtotal}</td>
            </tr>
          `).join('')}
        </table>
        <div class="divider"></div>
        <table>
          <tr><td>Subtotal:</td><td class="right">R$ ${receipt.totais?.subtotal}</td></tr>
          <tr><td>Desconto:</td><td class="right">R$ ${receipt.totais?.desconto}</td></tr>
          <tr class="bold"><td>TOTAL:</td><td class="right">R$ ${receipt.totais?.total}</td></tr>
        </table>
        <div class="divider"></div>
        <div>Pagamento: ${receipt.pagamento?.forma}</div>
        <div class="divider"></div>
        <div class="center">Obrigado pela preferência!</div>
        <script>window.print();</script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Colunas do carrinho
  const cartColumns = [
    {
      id: "product_name",
      label: "Produto",
      render: (item) => (
        <Typography fontWeight={600}>{item.product_name}</Typography>
      ),
    },
    {
      id: "unit_price",
      label: "Preço Unit.",
      render: (item) => formatMoney(item.unit_price),
    },
    {
      id: "quantity",
      label: "Quantidade",
      align: "center",
      render: (item) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => updateQuantity(item.product_id, -1)}
            data-testid={`decrease-qty-${item.product_id}`}
          >
            <Minus size={14} />
          </IconButton>
          <Typography
            sx={{ minWidth: 30, textAlign: "center" }}
            fontWeight={600}
          >
            {item.quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => updateQuantity(item.product_id, 1)}
            data-testid={`increase-qty-${item.product_id}`}
          >
            <Plus size={14} />
          </IconButton>
        </Box>
      ),
    },
    {
      id: "total",
      label: "Total",
      render: (item) => (
        <Typography fontWeight={700}>
          {formatMoney(item.total)}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "Ações",
      align: "right",
      render: (item) => (
        <IconButton
          color="error"
          size="small"
          onClick={() => removeFromCart(item.product_id)}
          data-testid={`remove-item-${item.product_id}`}
        >
          <Trash2 size={14} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box
      data-testid="pdv-page"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
        gap: 3,
        height: "calc(100vh - 200px)",
      }}
    >
      {/* Coluna esquerda */}
      <Box>
        {/* Buscar Produto */}
        <Paper sx={{ mb: 3 }}>
          <Box
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h6">Buscar Produto</Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                gap: 2,
                width: "100%",
              }}
            >
              <Autocomplete
                options={productOptions}
                value={null}
                loading={searchingProducts}
                fullWidth
                sx={{ width: "100%" }}
                onChange={(event, value) => {
                  if (value) {
                    addToCart(value);
                    setSearchTerm("");
                    setProductOptions([]);
                  }
                }}
                inputValue={searchTerm}
                onInputChange={(event, newInputValue) => {
                  setSearchTerm(newInputValue);
                }}
                filterOptions={(x) => x}
                getOptionLabel={(option) =>
                  option?.name
                    ? `${option.name}${
                        option.barcode ? ` (cód: ${option.barcode})` : ""
                      }`
                    : ""
                }
                noOptionsText={
                  searchTerm.trim().length < 2
                    ? "Digite ao menos 2 caracteres"
                    : "Nenhum produto encontrado"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Código de barras ou nome do produto"
                    size="small"
                    fullWidth
                    data-testid="pdv-search-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        searchProduct();
                      }
                    }}
                  />
                )}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={searchProduct}
                startIcon={<Search size={16} />}
                data-testid="pdv-search-btn"
                sx={{ minWidth: 140 }}
              >
                Buscar
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Carrinho */}
        <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h6">Carrinho de Compras</Typography>
          </Box>

          {cart.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 1.5,
              }}
            >
              <ShoppingCart size={64} />
              <Typography variant="h6">Carrinho vazio</Typography>
              <Typography variant="body2">
                Busque e adicione produtos ao carrinho
              </Typography>
            </Box>
          ) : (
            <DataTable
              columns={cartColumns}
              rows={cart}
              loading={false}
              getRowId={(row) => row.product_id}
              getRowTestId={(row) => `cart-item-${row.product_id}`}
            />
          )}
        </Paper>
      </Box>

      {/* Coluna direita – Finalizar Venda / Orçamento */}
      <Box>
        <Paper>
          <Box
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h6">
              {isBudget ? "Salvar Orçamento" : "Finalizar Venda"}
            </Typography>
          </Box>

          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="customer-label">Cliente (Opcional)</InputLabel>
              <Select
                labelId="customer-label"
                label="Cliente (Opcional)"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                data-testid="pdv-customer-select"
              >
                <MenuItem value="">Venda sem cliente</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="payment-label">Método de Pagamento</InputLabel>
              <Select
                labelId="payment-label"
                label="Método de Pagamento"
                value={paymentMethod}
                onChange={(e) => {
                  const nextPaymentMethod = e.target.value;
                  setPaymentMethod(nextPaymentMethod);

                  if (nextPaymentMethod === "promissoria") {
                    resetPromissoryData();
                  }
                }}
                data-testid="pdv-payment-select"
              >
                <MenuItem value="cash">Dinheiro</MenuItem>
                <MenuItem value="credit">Cartão de Crédito</MenuItem>
                <MenuItem value="debit">Cartão de Débito</MenuItem>
                <MenuItem value="pix">PIX</MenuItem>
                <MenuItem value="promissoria">Promissória</MenuItem>
              </Select>
            </FormControl>

            {paymentMethod === "promissoria" && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "warning.50",
                  border: "1px solid",
                  borderColor: "warning.main",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Dados da Promissória
                </Typography>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Parcelar?</InputLabel>
                  <Select
                    value={promissoryData.parcelar ? "sim" : "nao"}
                    label="Parcelar?"
                    onChange={(e) =>
                      handlePromissoryChange("parcelar", e.target.value === "sim")
                    }
                  >
                    <MenuItem value="nao">Não - Pagamento único</MenuItem>
                    <MenuItem value="sim">Sim - Parcelar</MenuItem>
                  </Select>
                </FormControl>

                {promissoryData.parcelar && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantidade de Parcelas"
                    value={promissoryData.parcelas}
                    onChange={(e) =>
                      handlePromissoryChange("parcelas", parseInt(e.target.value, 10) || 1)
                    }
                    inputProps={{ min: 1, max: 24 }}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}

                <Typography variant="body2" sx={{ mb: 1 }}>
                  {promissoryData.parcelar
                    ? "Datas das parcelas:"
                    : "Data de vencimento:"}
                </Typography>

                {(promissoryData.datas || [])
                  .slice(
                    0,
                    promissoryData.parcelar
                      ? Math.max(1, Number(promissoryData.parcelas) || 1)
                      : 1
                  )
                  .map((data, index) => (
                    <TextField
                      key={`${index}-${data}`}
                      fullWidth
                      type="date"
                      label={`Parcela ${index + 1}`}
                      value={data}
                      onChange={(e) => handlePromissoryDateChange(index, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ mb: index === promissoryData.datas.length - 1 ? 0 : 1 }}
                    />
                  ))}
              </Box>
            )}

            <TextField
              label="Desconto (R$)"
              type="number"
              size="small"
              value={discount}
              onChange={(e) =>
                setDiscount(parseFloat(e.target.value) || 0)
              }
              inputProps={{ step: "0.01" }}
              data-testid="pdv-discount-input"
            />

            {/* Toggle de orçamento */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isBudget}
                  onChange={(e) => setIsBudget(e.target.checked)}
                  color="primary"
                  data-testid="pdv-budget-checkbox"
                />
              }
              label="Salvar como orçamento (não emitir nota fiscal)"
            />

            {/* Opções de finalização (só aparecem se não for orçamento) */}
            {!isBudget && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={gerarNotaFiscal}
                      onChange={(e) => setGerarNotaFiscal(e.target.checked)}
                      color="primary"
                      size="small"
                      data-testid="pdv-gerar-nf-checkbox"
                    />
                  }
                  label={<Typography variant="body2">Gerar Nota Fiscal</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={imprimirComprovante}
                      onChange={(e) => setImprimirComprovante(e.target.checked)}
                      color="primary"
                      size="small"
                      data-testid="pdv-imprimir-checkbox"
                    />
                  }
                  label={<Typography variant="body2">Imprimir Comprovante</Typography>}
                />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography>Subtotal:</Typography>
              <Typography fontWeight={600} data-testid="pdv-subtotal">
                {formatMoney(calculateSubtotal())}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
                color: "error.main",
              }}
            >
              <Typography>Desconto:</Typography>
              <Typography fontWeight={600} data-testid="pdv-discount-value">
                - {formatMoney(discount)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6">Total:</Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                color="primary"
                data-testid="pdv-total"
              >
                {formatMoney(calculateTotal())}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="success"
              fullWidth
              startIcon={<CreditCard size={16} />}
              onClick={handleFinalizeClick}
              disabled={cart.length === 0 || finalizing}
              data-testid="pdv-finalize-btn"
            >
              {finalizing
                ? isBudget
                  ? "Salvando orçamento..."
                  : "Finalizando..."
                : isBudget
                ? "Salvar Orçamento"
                : "Finalizar Venda"}
            </Button>

            {!currentCashier && (
              <Alert severity="warning" sx={{ mt: 2, fontSize: 13 }}>
                <strong>Atenção:</strong> Nenhum caixa aberto. Vá para Caixa e
                abra um novo caixa.
              </Alert>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Dialog de senha do supervisor (para desconto > 3%) */}
      <Dialog
        open={supervisorDialogOpen}
        onClose={handleSupervisorCancel}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Autorização do supervisor</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            O desconto informado é maior que <strong>3% do subtotal</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Para continuar, informe a senha de um{" "}
            <strong>supervisor ou administrador</strong>.
          </Typography>

          <TextField
            label="Senha do supervisor"
            type="password"
            fullWidth
            margin="normal"
            value={supervisorPassword}
            onChange={(e) => setSupervisorPassword(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSupervisorCancel}
            disabled={verifyingSupervisor || finalizing}
          >
            Cancelar (zera desconto)
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSupervisorConfirm}
            disabled={verifyingSupervisor || finalizing}
          >
            {verifyingSupervisor ? "Validando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
